import './instrument'; // Initialize Sentry before everything else
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import './i18n'; // Import i18n configuration
import { initializeSystem, systemInitializer } from './services/system-initializer';
import { checkCriticalEnvironment, logEnvironmentCheck, validateEnvironment } from './utils/environmentCheck';
import { logger } from './utils/logger';
// Import and configure dayjs globally for timezone support
import './utils/dateUtils';

// Check environment configuration
const envResult = validateEnvironment();
logEnvironmentCheck(envResult);

// Check for critical environment issues
try {
  checkCriticalEnvironment();
} catch (error) {
  logger.error('Critical environment configuration error', error as Error);
  // In production, this will prevent app startup
}

// Log application startup
logger.info('Application starting', {
  module: 'main',
  action: 'startup',
  metadata: {
    environment: import.meta.env.MODE,
    version: import.meta.env.VITE_APP_VERSION || '1.0.0'
  }
});

// Initialize system components with proper sequencing
initializeSystem({
  enablePerformanceOptimization: true,
  enableCaching: true,
  enableMonitoring: true,
  development: import.meta.env.MODE === 'development'
}).then(() => {
  logger.info('System initialization completed successfully', {
    module: 'main',
    action: 'initialization'
  });
}).catch(_ => {
  logger.warn('System initialization failed (non-critical)', {
    module: 'main',
    action: 'initialization'
  });
  // Don't prevent app startup
});

// Note: Global error/unhandledrejection handlers are managed by Sentry and the Logger class.
// No need for duplicate handlers here.

// Log performance metrics on page load
window.addEventListener('load', () => {
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    
    logger.performance('page-load', loadTime, {
      module: 'performance',
      metadata: {
        domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
        domComplete: timing.domComplete - timing.navigationStart
      }
    });
  }
});

// Setup graceful shutdown handler
window.addEventListener('beforeunload', () => {
  systemInitializer.shutdown();
});

if (import.meta.env.DEV) {
  const s = document.createElement('script');
  s.src = 'https://cdn.screen2prompt.supalab.dev/v1/screen2prompt.js';
  document.head.appendChild(s);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
